// src/pages/LiffAuth.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import StageFrame from '../components/StageFrame';
import FullBleed from '../components/FullBleed';
import bg from '../assets/card.png';
import cardApply from '../assets/card_apply.png';
import { LiffLineProvider } from '../context/LiffLineContext';
import { hasLineClaimIntent, redirectToCouponClaimIfNeeded } from '../utils/couponLiff';

axios.defaults.withCredentials = true;

const LIFF_SDK_URL = 'https://static.line-scdn.net/liff/edge/2/sdk.js';
const HARD_CODED_LIFF_ID = '2007543734-5n4NpyXV';
const UNBOUND_REDIRECT = '/link?mode=register';
const TAB_POS = { x: 70, y: 250, w: 940 };
const LIFF_QUERY_KEYS = [
  'liff.state',
  'liff.referrer',
  'liffClientId',
  'liffSdkVersion',
  'liffChannelId',
  'liff.subWindow',
  'liff.referrerAppId',
];

const getLiffId = () =>
  (process.env.REACT_APP_LIFF_ID || window.__LIFF_ID__ || HARD_CODED_LIFF_ID).trim();

function ensureLiffSdk() {
  if (window.liff) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existed = document.querySelector(`script[src="${LIFF_SDK_URL}"]`);
    if (existed) {
      existed.addEventListener('load', () => resolve());
      existed.addEventListener('error', () => reject(new Error('LOAD_LIFF_SDK_FAIL')));
      return;
    }
    const s = document.createElement('script');
    s.src = LIFF_SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('LOAD_LIFF_SDK_FAIL'));
    document.head.appendChild(s);
  });
}

function readQuery() {
  const qs = new URLSearchParams(window.location.search);
  return {
    fromLine: (qs.get('from') || '').toLowerCase() === 'line',
    // 只拿來判斷「要不要嘗試 LIFF」，不當作身份使用
    lineIdHint: qs.get('lineid') || qs.get('line_sub') || qs.get('ls') || null,
  };
}

export default function LiffAuth({ children }) {
  const [ready, setReady] = useState(false);
  const [lineSubForChild, setLineSubForChild] = useState(null);
  const [showChoice, setShowChoice] = useState(false); // 只給純瀏覽器
  const [busy, setBusy] = useState(false);

  const liffId = useMemo(getLiffId, []);
  const origin = useMemo(
    () => window.location.origin || `${window.location.protocol}//${window.location.host}`,
    []
  );
  const stripQueryParams = useCallback(
    (search, { keepLineFlag = false } = {}) => {
      const usp = new URLSearchParams(search || '');
      LIFF_QUERY_KEYS.forEach((key) => usp.delete(key));
      const fromVal = usp.get('from');
      if (!keepLineFlag && fromVal && fromVal.toLowerCase() === 'line') {
        usp.delete('from');
      }
      return usp;
    },
    []
  );
  const sanitizePath = useCallback(
    (raw) => {
      if (!raw) return null;
      try {
        const absolute = raw.startsWith('http') ? new URL(raw) : new URL(raw, origin);
        const usp = stripQueryParams(absolute.search, { keepLineFlag: false });
        const query = usp.toString();
        return `${absolute.pathname}${query ? `?${query}` : ''}`;
      } catch {
        return null;
      }
    },
    [origin, stripQueryParams]
  );
  const buildCurrentPath = useCallback(
    (addLineFlag = false) => {
      const usp = stripQueryParams(window.location.search, { keepLineFlag: addLineFlag });
      if (addLineFlag) usp.set('from', 'line');
      const query = usp.toString();
      const path = window.location.pathname || '/';
      return `${path}${query ? `?${query}` : ''}`;
    },
    [stripQueryParams]
  );
  const rememberReturnPath = useCallback((path) => {
    const safe = path || '/card';
    try {
      sessionStorage.setItem('liff:return', safe);
    } catch {}
    return safe;
  }, []);
  const getRememberedPath = useCallback(() => {
    try {
      return sessionStorage.getItem('liff:return');
    } catch {
      return null;
    }
  }, []);
  const [forceLineLogin, setForceLineLogin] = useState(() => {
    try {
      return sessionStorage.getItem('liff:autoLine') === '1';
    } catch {
      return false;
    }
  });
  const clearForceLineLogin = useCallback(() => {
    try { sessionStorage.removeItem('liff:autoLine'); } catch {}
    setForceLineLogin(false);
  }, []);
  const redirectToPath = useCallback(
    (raw) => {
      const sanitized = sanitizePath(raw) || getRememberedPath() || '/card';
      rememberReturnPath(sanitized);
      window.location.replace(toAbsoluteUrl(sanitized));
    },
    [sanitizePath, getRememberedPath, rememberReturnPath]
  );
  useEffect(() => {
    if (redirectToCouponClaimIfNeeded()) return;
    const raw = window.location.pathname + window.location.search;
    const current = sanitizePath(raw) || '/card';
    rememberReturnPath(current);
    if (current && current !== raw) {
      window.history.replaceState({}, '', current);
    }
  }, [sanitizePath, rememberReturnPath]);
  const toAbsoluteUrl = (url) => {
    if (!url) return origin;
    if (/^https?:\/\//i.test(url)) return url;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return `${origin}${normalized}`;
  };
  
  // 构建 deepLink，保留完整的 URL（包括查询参数）
  const deepLink = useMemo(() => {
    const pathAndQuery = buildCurrentPath(true);
    const state = encodeURIComponent(pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`);
    return {
      scheme: `line://app/${liffId}?liff.state=${state}`,
      universal: `https://liff.line.me/${liffId}?liff.state=${state}`,
    };
  }, [liffId, buildCurrentPath]);

  const goRegister = useCallback(
    (lineSub) => {
      const cleanPath = sanitizePath(window.location.pathname + window.location.search) || '/card';
      rememberReturnPath(cleanPath);
      const needsFrom = cleanPath.includes('/coupon-claim');
      const fromParam = needsFrom ? `&from=${encodeURIComponent(cleanPath)}` : '';
      const url = lineSub
        ? `${UNBOUND_REDIRECT}&ls=${encodeURIComponent(lineSub)}${fromParam}`
        : `${UNBOUND_REDIRECT}${fromParam}`;
      window.location.replace(url);
    },
    [sanitizePath, rememberReturnPath]
  );

  useEffect(() => {
    (async () => {
      // NEW(1): 先檢查現有會話（PIN 或 LINE 登入都能通過）
     try {
       await axios.get('/api/me');        // 200 代表已登入
       clearForceLineLogin();
       if (redirectToCouponClaimIfNeeded()) return;
       const couponLineClaim =
         (window.location.pathname || '').includes('/coupon-claim') && hasLineClaimIntent();
       if (!couponLineClaim) {
         setReady(true);                  // 直接放行 children
         return;                          // 不要再跑下面的登入流程
       }
       // LINE 領券仍須走 LIFF 取得 line_sub（PIN 登入 cookie 不足以 claim-by-line）
     } catch (_) {
       // 沒登入才繼續往下
     }

      // 1) 決定此頁是否「應該走 LINE 流程」
      const { fromLine, lineIdHint } = readQuery();
      const ua = (navigator.userAgent || '').toLowerCase();
      const uaLooksLikeLine = ua.includes(' line') || ua.includes('liff');
      const shouldTryLiff =
        uaLooksLikeLine || fromLine || !!lineIdHint || forceLineLogin || hasLineClaimIntent();

      // 2) 純瀏覽器（沒有任何 LINE 意圖）→ 顯示雙登入（初始頁）
      if (!shouldTryLiff) {
        if (forceLineLogin) {
          setBusy(true);
          clearForceLineLogin();
          window.location.assign(deepLink.universal);
          return;
        }
        setShowChoice(true);
        return;
      }
      // 3) 「LINE 流程」：一定要以本次 LIFF 取得的 idToken 為準
      //    /api/me 已回 401，不需再呼叫 logout；/auth/line/token 會覆寫舊 cookie
      try {
        setBusy(true);
        await ensureLiffSdk();
        const liff = window.liff;
        await liff.init({ liffId, withLoginOnExternalBrowser: true });

        const inClient =
          (typeof liff.isInClient === 'function' && liff.isInClient() === true) ||
          (liff.getContext?.()?.type && liff.getContext().type !== 'external');

        // 檢查是否有 liff.state 參數（從外部瀏覽器跳轉回來時會有）
        const usp = new URLSearchParams(window.location.search);
        const liffState = usp.get('liff.state');
        let targetPath = sanitizePath(window.location.pathname + window.location.search);
        if (liffState) {
          try {
            const decoded = decodeURIComponent(liffState);
            const sanitized = sanitizePath(decoded);
            if (sanitized) {
              targetPath = sanitized;
              rememberReturnPath(sanitized);
              window.history.replaceState({}, '', sanitized);
            }
          } catch (e) {
            console.warn('Failed to decode liff.state:', e);
          }
        }

        if (inClient && !liff.isLoggedIn()) {
          // 保留完整的 URL（包括所有查詢參數）
          // 如果有 liff.state，使用它；否則使用當前 URL
          const redirectUrl = toAbsoluteUrl(targetPath || window.location.pathname + window.location.search);
          clearForceLineLogin();
          liff.login({ redirectUri: redirectUrl });
          return;
        }

        const idToken = liff.getIDToken();
        clearForceLineLogin();
        if (!idToken) { goRegister(null); return; }
        let lineSub = null;
        try {
          const decoded = window.liff?.getDecodedIDToken?.();
          lineSub = decoded?.sub || null;
          if (lineSub) localStorage.setItem('line_sub', lineSub);
        } catch (_) {}


        // 拿到 token → 與後端交換，若未綁定則導去註冊
        const { data } = await axios.post('/auth/line/token', { id_token: idToken });
        if (data?.sub) {
          lineSub = data.sub;
          try { localStorage.setItem('line_sub', lineSub); } catch { /* ignore */ }
          setLineSubForChild(lineSub);
        } else if (lineSub) {
          setLineSubForChild(lineSub);
        }

        if (!data?.bound) {
          const couponLineClaim =
            (window.location.pathname || '').includes('/coupon-claim') && hasLineClaimIntent();
          if (couponLineClaim) {
            setReady(true);
            return;
          }
          goRegister(lineSub);
          return;
        }

        // 登入成功後，檢查是否有 from 參數或 liff.state（來自 coupon-claim），有的話導回去
        const currentUsp = new URLSearchParams(window.location.search);
        const from = currentUsp.get('from');
        const liffStateAfterLogin = currentUsp.get('liff.state');
        
        // 優先檢查 liff.state（從外部瀏覽器跳轉回來）
        if (liffStateAfterLogin) {
          try {
            const decodedState = decodeURIComponent(liffStateAfterLogin);
            redirectToPath(decodedState);
            return;
          } catch (e) {
            console.warn('Failed to decode liff.state after login:', e);
          }
        }
        
        // 其次檢查 from 參數
        if (from) {
          try {
            const decodedFrom = decodeURIComponent(from);
            redirectToPath(decodedFrom);
            return;
          } catch (e) {
            console.warn('Failed to decode from param:', e);
          }
        }
        
        // 如果當前路徑已經是 coupon-claim，直接放行（讓 CouponClaim 組件處理）
        const currentPathClean = sanitizePath(window.location.pathname + window.location.search);
        if (currentPathClean && currentPathClean.includes('/coupon-claim')) {
          rememberReturnPath(currentPathClean);
          setReady(true);
          return;
        }
        const storedPath = getRememberedPath();
        if (storedPath && storedPath.includes('/coupon-claim')) {
          window.history.replaceState({}, '', storedPath);
          setReady(true);
          return;
        }

        setReady(true);
      } catch (e) {
        console.warn('[LiffAuth] LINE flow error:', e);
        clearForceLineLogin();
        const couponLineClaim =
          (window.location.pathname || '').includes('/coupon-claim') && hasLineClaimIntent();
        if (couponLineClaim) {
          setLineSubForChild(null);
          setReady(true);
          return;
        }
        const safePath = sanitizePath(window.location.pathname + window.location.search) || getRememberedPath() || '/card';
        const fromParam = safePath.includes('/coupon-claim') ? `&from=${encodeURIComponent(safePath)}` : '';
        window.location.replace(`${UNBOUND_REDIRECT}${fromParam}`);
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liffId]);

  if (ready) {
    return (
      <LiffLineProvider lineSub={lineSubForChild} liffReady>
        {children ?? null}
      </LiffLineProvider>
    );
  }

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px }) => (
          <>
            <style>{`
              .apply-tab { position:absolute; left:${px(TAB_POS.x)}px; top:${px(TAB_POS.y)}px; width:${px(TAB_POS.w)}px; height:auto; pointer-events:none; }
              .la-wrap { position:absolute; left:${px(140)}px; right:${px(140)}px; top:${px(500)}px; color:#111; font-family:"CreamFont","CSong3HK",system-ui,-apple-system,sans-serif; }
              .la-h1 { font-weight:800; font-size:${px(70)}px; line-height:1.12; margin:0 0 ${px(10)}px; }
              .la-p  { font-size:${px(36)}px; margin:0 0 ${px(18)}px; color:#222; }
              .la-grid { display:grid; gap:${px(12)}px; margin-top:${px(6)}px; }
              .la-btn { width:100%; font-size:${px(42)}px; font-weight:900; padding:${px(14)}px; border-radius:${px(10)}px; border:1px solid #111; background:transparent; color:#111; cursor:pointer; }
              .la-btn.primary { background:#00B900; color:#fff; }
              .la-btn:active { transform: translateY(1px); }
              .la-spinner { width:${px(48)}px; height:${px(48)}px; border:${px(6)}px solid #eee; border-top-color:#111; border-radius:50%; margin:${px(18)}px 0; animation: la-spin 1s linear infinite; }
              @keyframes la-spin { to { transform: rotate(360deg); } }
            `}</style>

            <img className="apply-tab" src={cardApply} alt="" />

            {showChoice ? (
              <div className="la-wrap">
                <h1 className="la-h1">選擇登入方式</h1>
                <p className="la-p">你可以使用 LINE 或 電話 + PIN 碼 登入</p>
                <div className="la-grid">
                  <button
                    disabled={busy}
                    className="la-btn primary"
                    onClick={() => {
                      if (busy) return;
                      // 直接使用 LINE 官方推薦的 universal link，確保瀏覽器不會改寫 scheme
                      window.location.assign(deepLink.universal);
                    }}
                    style={{ opacity: busy ? .6 : 1 }}
                  >
                    {busy ? '準備中…' : '使用 LINE 登入'}
                  </button>
                  <button className="la-btn" onClick={() => {
                    const cleanPath = sanitizePath(window.location.pathname + window.location.search) || '/card';
                    const fromParam = cleanPath.includes('/coupon-claim') ? `?from=${encodeURIComponent(cleanPath)}` : '';
                    window.location.assign(`/pass-login${fromParam}`);
                  }}>
                    使用 電話 + PIN 碼 登入
                  </button>
                </div>
              </div>
            ) : (
              <div className="la-wrap" style={{ textAlign:'center' }}>
                <h1 className="la-h1" style={{ marginBottom: 0 }}>登入中…</h1>
                <div className="la-spinner" />
                <p className="la-p" style={{ opacity:.6, marginTop:0 }}>
                  正在與 LINE 驗證，請稍候
                </p>
              </div>
            )}
          </>
        )}
      />
    </FullBleed>
  );
}
